import { TrackingData } from './TrackingContext';

const PAGE_KEY = 'hybridPDPAnalyticsPage';
const NAVIGATION_KEY = 'hybridPDPAnalyticsNavigation';
const PDP_NAVIGATION_KEY = 'hybridPDPAnalyticsPdpNavigation';
const EXPIRY_MS = 5 * 60 * 1000;

interface Attribution {
  data: TrackingData;
  createdAt: number;
  pathname?: string;
  page?: 'pdp' | 'cart';
  productID?: string;
}

const read = (key: string): Attribution | undefined => {
  try {
    const record = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (
      !record?.data ||
      typeof record.createdAt !== 'number' ||
      (key === NAVIGATION_KEY && Date.now() - record.createdAt > EXPIRY_MS)
    )
      return;
    return record;
  } catch {
    return;
  }
};

const write = (key: string, value: Attribution) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Attribution must never block navigation when storage is unavailable.
  }
};

export const setHybridPDPPageAttribution = (data: TrackingData) => {
  if (typeof window === 'undefined') return;
  write(PAGE_KEY, { data, pathname: window.location.pathname, createdAt: Date.now() });
};

export const clearHybridPDPPageAttribution = () => {
  try {
    sessionStorage.removeItem(PAGE_KEY);
  } catch {
    // Storage may be disabled.
  }
};

export const getHybridPDPPageAttribution = (): TrackingData => {
  const record = read(PAGE_KEY);
  return typeof window !== 'undefined' && record?.pathname === window.location.pathname
    ? record.data
    : {};
};

export const setHybridPDPNavigationAttribution = (
  data: TrackingData,
  page: 'pdp' | 'cart',
  productID?: string
) => {
  write(NAVIGATION_KEY, { data, page, productID, createdAt: Date.now() });
};

export const consumeHybridPDPNavigationAttribution = (
  page: 'pdp' | 'cart',
  productID?: string
): TrackingData => {
  const record = read(NAVIGATION_KEY);
  if (page === 'pdp') {
    try {
      sessionStorage.removeItem(PDP_NAVIGATION_KEY);
    } catch {
      // Storage must not block page-view tracking.
    }
  }
  if (record?.page !== page || record.productID !== productID) return {};
  try {
    sessionStorage.removeItem(NAVIGATION_KEY);
  } catch {
    return {};
  }
  if (page === 'pdp') {
    write(PDP_NAVIGATION_KEY, {
      data: {
        siteNavigation: record.data.siteNavigation,
        siteSubNavigation: record.data.siteSubNavigation,
      },
      productID,
      pathname: window.location.pathname,
      createdAt: Date.now(),
    });
  }
  return record.data;
};

export const getHybridPDPNavigationForAddToCart = (productID?: string): TrackingData => {
  const record = read(PDP_NAVIGATION_KEY);
  return typeof window !== 'undefined' && productID && record?.productID === productID &&
    record.pathname === window.location.pathname ? record.data : {};
};
